import { useEffect, useRef, useState } from 'react'
import { Check, Minus, Plus, X } from 'lucide-react'
import type { WireArticleBlock, WireInlineBlock } from '../../../api/types'

type ListBlock = Extract<WireArticleBlock, { type: 'list' }>
type ListItem = ListBlock['items'][number]

// Flatten WireInlineBlock[] → plain string for editing
function itemsToStrings(items: ListItem[]): string[] {
  return items.map(item =>
    item.children
      .flatMap(n => (n.type === 'span' || n.type === 'link') ? [n.text] : [])
      .join('')
  )
}

// Plain string → WireInlineBlock[]
function stringToChildren(text: string): WireInlineBlock[] {
  return text ? [{ type: 'span', text }] : []
}

interface Props {
  block: ListBlock
  onSave: (items: ListItem[]) => void
  onCancel: () => void
}

export function ListBlockEditor({ block, onSave, onCancel }: Props) {
  const [texts, setTexts] = useState<string[]>(() => {
    const s = itemsToStrings(block.items)
    return s.length > 0 ? s : ['']
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleSave()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [texts]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSave() {
    const items: ListItem[] = texts
      .filter(t => t.trim().length > 0)
      .map((t, i) => ({
        id: block.items[i]?.id ?? crypto.randomUUID(),
        type: 'list-item' as const,
        children: stringToChildren(t),
      }))
    onSave(items)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>, idx: number) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const newTexts = [...texts.slice(0, idx + 1), '', ...texts.slice(idx + 1)]
      setTexts(newTexts)
      setTimeout(() => inputRefs.current[idx + 1]?.focus(), 0)
    }
    if (e.key === 'Backspace' && texts[idx] === '' && texts.length > 1) {
      e.preventDefault()
      const newTexts = texts.filter((_, i) => i !== idx)
      setTexts(newTexts)
      setTimeout(() => inputRefs.current[Math.max(0, idx - 1)]?.focus(), 0)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      inputRefs.current[Math.min(texts.length - 1, idx + 1)]?.focus()
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      inputRefs.current[Math.max(0, idx - 1)]?.focus()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }

  function updateText(idx: number, value: string) {
    setTexts(prev => prev.map((t, i) => i === idx ? value : t))
  }

  function addItem() {
    const newTexts = [...texts, '']
    setTexts(newTexts)
    setTimeout(() => inputRefs.current[newTexts.length - 1]?.focus(), 0)
  }

  function removeItem(idx: number) {
    if (texts.length <= 1) { setTexts(['']); return }
    const newTexts = texts.filter((_, i) => i !== idx)
    setTexts(newTexts)
    setTimeout(() => inputRefs.current[Math.max(0, idx - 1)]?.focus(), 0)
  }

  const Bullet = block.style === 'unordered' ? '•' : null

  return (
    <div
      ref={containerRef}
      className="rounded border-2 border-accent-primary-solid bg-surface p-1.5"
    >
      <div className="space-y-1">
        {texts.map((text, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span className="shrink-0 w-4 text-center text-sm text-fg-secondary select-none">
              {Bullet ?? `${idx + 1}.`}
            </span>
            <input
              ref={el => { inputRefs.current[idx] = el }}
              type="text"
              value={text}
              onChange={e => updateText(idx, e.target.value)}
              onKeyDown={e => handleKeyDown(e, idx)}
              placeholder="List item…"
              className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-secondary"
              aria-label={`List item ${idx + 1}`}
            />
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); removeItem(idx) }}
              className="shrink-0 rounded p-0.5 text-fg-secondary opacity-0 transition-opacity hover:bg-fill hover:text-fg group-focus-within:opacity-100"
              title="Remove item"
            >
              <Minus size={11} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex items-center gap-1 border-t border-line pt-1.5">
        <button
          type="button"
          onMouseDown={e => { e.preventDefault(); addItem() }}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-fg-secondary hover:bg-fill hover:text-fg"
          title="Add item"
        >
          <Plus size={10} /> Add item
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onMouseDown={e => { e.preventDefault(); onCancel() }}
          className="rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg"
          title="Cancel (Esc)"
        >
          <X size={13} />
        </button>
        <button
          type="button"
          onMouseDown={e => { e.preventDefault(); handleSave() }}
          className="rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg"
          title="Save"
        >
          <Check size={13} />
        </button>
      </div>
    </div>
  )
}
